// Blog API client for Ghost integration

export interface BlogPost {
  id: string;
  title: string;
  slug: string;
  brief: string;
  coverImage: string | null;
  publishedAt: string;
  readTimeInMinutes: number;
  tags: { name: string; slug: string }[];
}

export interface BlogPostDetail extends BlogPost {
  content: string;
  updatedAt: string;
  seoTitle: string;
  seoDescription: string;
  ogImage: string | null;
}

export interface BlogTag {
  name: string;
  slug: string;
  count: number;
}

export interface BlogPostsResponse {
  posts: BlogPost[];
  pageInfo: {
    hasNextPage: boolean;
    endCursor: string | null;
  };
}

export interface BlogPostsByTagResponse {
  posts: BlogPost[];
  tag: string;
}

const API_BASE = '/api/blog';

let _retryBaseDelay = 3000;
export function _setRetryDelay(ms: number) { _retryBaseDelay = ms; }

async function fetchWithRetry(url: string, retries = 3): Promise<Response> {
  for (let attempt = 0; attempt <= retries; attempt++) {
    try {
      const response = await fetch(url);
      if (response.ok || (response.status >= 400 && response.status < 500)) return response;
      if (attempt === retries) return response;
    } catch (error) {
      if (attempt === retries) throw error;
    }
    if (_retryBaseDelay > 0) {
      await new Promise(resolve => setTimeout(resolve, _retryBaseDelay * Math.pow(2, attempt)));
    }
  }
  throw new Error(`Request to ${url} failed after ${retries + 1} attempts`);
}

export async function fetchBlogPosts(first = 50): Promise<BlogPostsResponse> {
  const params = new URLSearchParams({ first: String(first) });
  const response = await fetchWithRetry(`${API_BASE}/posts?${params}`);
  if (!response.ok) {
    throw new Error('Failed to fetch blog posts');
  }
  return response.json();
}

export async function fetchBlogPost(slug: string): Promise<BlogPostDetail> {
  const response = await fetchWithRetry(`${API_BASE}/posts/${encodeURIComponent(slug)}`);
  if (!response.ok) {
    if (response.status === 404) {
      throw new Error('Blog post not found');
    }
    throw new Error('Failed to fetch blog post');
  }
  return response.json();
}

export async function fetchBlogTags(): Promise<BlogTag[]> {
  const response = await fetchWithRetry(`${API_BASE}/tags`);
  if (!response.ok) {
    throw new Error('Failed to fetch blog tags');
  }
  return response.json();
}

export async function fetchBlogPostsByTag(tag: string, first = 20): Promise<BlogPostsByTagResponse> {
  const params = new URLSearchParams({ first: String(first) });
  const response = await fetchWithRetry(`${API_BASE}/posts/tag/${encodeURIComponent(tag)}?${params}`);
  if (!response.ok) {
    throw new Error('Failed to fetch blog posts by tag');
  }
  return response.json();
}

export function formatDate(dateString: string): string {
  return new Date(dateString).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
}
